import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Alert,
  Container,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useHomeIntelligenceContext } from '../contexts/HomeIntelligenceContext';
import { useHomeDetailsContext } from '../contexts/HomeDetailsContext';
import { usePropertyPublicData } from '../hooks/usePropertyPublicData';
import { hasValidAddress } from '../utils/homeUtils';
import { transformIntelligenceData } from '../utils/intelligenceTransforms';
import { getIntelligenceColors } from '../utils/intelligenceTheme';
import GradePill from '../components/intelligence/elegant/GradePill';
import Meter from '../components/intelligence/elegant/Meter';
import DriverRow from '../components/intelligence/elegant/DriverRow';
import InsightChip from '../components/intelligence/elegant/InsightChip';
import RiskTag from '../components/intelligence/elegant/RiskTag';
import PropertyIntelligenceSkeleton from '../components/intelligence/elegant/PropertyIntelligenceSkeleton';
import IntelligenceReportHeader from '../components/intelligence/IntelligenceReportHeader';
import OwnerInformationCard from '../components/intelligence/OwnerInformationCard';

export const PropertyIntelligencePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const intelColors = getIntelligenceColors(isDark);
  const { intelligence, isLoading, error, address } = useHomeIntelligenceContext();
  const { homeData, homeId } = useHomeDetailsContext();
  const hasAddress = hasValidAddress(homeData?.details);

  const {
    propertyData,
    isLoading: isLoadingOwner,
    error: ownerError,
  } = usePropertyPublicData({
    address: address || '',
    enabled: !!address,
  });

  const transformedData = useMemo(() => {
    if (!intelligence || !address) return null;
    return transformIntelligenceData(intelligence, address);
  }, [intelligence, address]);

  if (isLoading) {
    return <PropertyIntelligenceSkeleton />;
  }

  if (error || !transformedData) {
    const isAddressMissing = !hasAddress;

    return (
      <>
        <IntelligenceReportHeader />
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
            p: 4,
          }}
        >
          <Container maxWidth="lg">
            {isAddressMissing ? (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/home/${homeId}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit Home
                  </Button>
                }
              >
                <Typography variant="h6" gutterBottom>
                  Property Address Required
                </Typography>
                <Typography variant="body2">
                  To generate Property Intelligence, please add a valid address to this home.
                  Intelligence analysis requires location data to provide market insights, property assessments, and comparative analytics.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error">
                {error?.message || 'Failed to load property intelligence data'}
              </Alert>
            )}
          </Container>
        </Box>
      </>
    );
  }

  const { overall, overallLabel, percentileText, subscores, insights, risks } = transformedData;
  const sortedDrivers = [...subscores].sort((a, b) => b.score - a.score);
  const lowestDriver = [...subscores].sort((a, b) => a.score - b.score)[0];

  const backgroundColor = isDark ? intelColors!.background.primary : theme.palette.background.default;
  const textPrimary = isDark ? intelColors!.text.primary : theme.palette.text.primary;
  const textSecondary = isDark ? intelColors!.text.secondary : theme.palette.text.secondary;
  const textMuted = isDark ? intelColors!.text.muted : theme.palette.text.secondary;

  return (
    <>
      <IntelligenceReportHeader />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor,
          color: textPrimary,
        }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Main Header Card */}
        <Card
          sx={{
            background: isDark
              ? `linear-gradient(135deg, ${intelColors!.background.secondary} 0%, ${intelColors!.background.primary} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
            border: `1px solid ${isDark ? intelColors!.border.primary : theme.palette.divider}`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            boxShadow: theme.shadows[8],
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                  color: textMuted,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  mb: 2,
                  display: 'block',
                }}
              >
                Property Intelligence
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'end', gap: 3, mb: 2, justifyContent: 'center' }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '4rem', md: '6rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                    color: textPrimary,
                  }}
                >
                  {overall}
                </Typography>
                <Box sx={{ pb: 1 }}>
                  <GradePill score={overall} />
                </Box>
              </Box>

              <Typography variant="h6" sx={{ color: textPrimary, mb: 1, fontWeight: 500 }}>
                {overallLabel}
              </Typography>

              <Typography variant="body2" sx={{ color: textMuted, mb: 3 }}>
                {percentileText}
              </Typography>

              <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                <Meter score={overall} />
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Grid container spacing={2}>
                {insights.map((insight, index) => (
                  <Grid item xs={6} key={index}>
                    <InsightChip insight={insight} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons - Commented out until data is available */}
          {/* <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CompareArrows />}
              sx={(theme) => ({
                borderRadius: 3,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: theme.palette.mode === 'dark' ? '#a3e635' : '#84cc16',
                color: theme.palette.mode === 'dark' ? '#09090b' : '#fff',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? '#84cc16' : '#65a30d',
                },
              })}
            >
              Compare to Area
            </Button>
            <Button
              variant="outlined"
              startIcon={<Assessment />}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              See Assumptions
            </Button>
            <Button
              variant="outlined"
              startIcon={<BookmarkAdd />}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Save & Track
            </Button>
          </Box> */}
        </Card>

        {/* Drivers and Sidebar */}
        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: textSecondary }}>
                  What Drives This Score
                </Typography>
                <Typography variant="caption" sx={{ color: textMuted }}>
                  Sorted by score
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {sortedDrivers.map((driver) => (
                  <Grid item xs={12} sm={6} key={driver.key}>
                    <DriverRow driver={driver} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            {/* Top Opportunity */}
            <Card
              sx={{
                backgroundColor: isDark ? intelColors!.background.paper : alpha(theme.palette.background.paper, 0.4),
                border: `1px solid ${isDark ? intelColors!.border.primary : theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: textPrimary, mb: 1 }}>
                Top Opportunity
              </Typography>
              <Typography variant="body2" sx={{ color: textMuted, mb: 3 }}>
                Focusing on the lowest driver typically yields the greatest lift.
              </Typography>

              <Card
                sx={{
                  backgroundColor: isDark ? `${intelColors!.background.primary}80` : alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${isDark ? intelColors!.border.primary : theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
                    {lowestDriver.name}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: textPrimary }}>
                    {Math.round(lowestDriver.score)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Meter score={lowestDriver.score} />
                </Box>

                <Typography variant="body2" sx={{ color: textMuted, mb: 2 }}>
                  {lowestDriver.why}
                </Typography>

                {/* TODO: Implement "View Playbook" feature - detailed action plans and step-by-step guides
                    for improving each driver (e.g., Market Momentum, Property Condition, etc.) */}
                {/* <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                  }}
                >
                  View Playbook
                </Button> */}
              </Card>
            </Card>

            {/* Risk Flags */}
            <Card
              sx={{
                backgroundColor: isDark ? intelColors!.background.paper : alpha(theme.palette.background.paper, 0.4),
                border: `1px solid ${isDark ? intelColors!.border.primary : theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: textPrimary, mb: 3 }}>
                Risk Flags
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {risks.map((risk, index) => (
                  <RiskTag key={index} risk={risk} />
                ))}
              </Box>

              <Typography variant="caption" sx={{ color: textMuted, fontSize: '0.7rem' }}>
                Flags highlight considerations; confirm during inspection & due diligence.
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Owner Information Section */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} lg={4}>
            {ownerError && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Unable to load owner information
              </Alert>
            )}
            <OwnerInformationCard
              owner={propertyData?.owner || null}
              ownerOccupied={propertyData?.ownerOccupied ?? null}
              propertyAddress={propertyData?.formattedAddress || address}
              loading={isLoadingOwner && !propertyData}
            />
          </Grid>
        </Grid>

        {/* Methodology Section */}
        <Box sx={{ mt: 4 }}>
          <Accordion
            sx={{
              backgroundColor: isDark ? `${intelColors!.background.secondary}4D` : alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${isDark ? intelColors!.border.primary : theme.palette.divider}`,
              borderRadius: 3,
              '&:before': { display: 'none' },
              boxShadow: 'none',
              color: textPrimary,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: textPrimary }} />}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
                Methodology & Data Freshness
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ color: textMuted, fontSize: '0.875rem', lineHeight: 1.6 }}>
                <Typography variant="body2" sx={{ mb: 2, color: textMuted }}>
                  Scores are 0–100. Overall score is a weighted blend of the five drivers. Subscores reflect
                  current data; momentum uses trailing 30–90 day local trends. DOM, list-to-sale ratio, and
                  volatility feed into Market Momentum and Position.
                </Typography>
                <Typography variant="body2" sx={{ color: textMuted }}>
                  *Estimated overall lift is a rough signal for prioritization, not a guarantee. Re-run after
                  each change to update estimates.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default PropertyIntelligencePage;